import { useCallback } from "react";

import { CapabilitySet, ScopeLevel } from "@/types/capability.types";
import { hasAuditRead } from "@/utils/capability";

import { useLeagueCapabilities } from "../capability/useLeagueCapabilities";
import { useOrganizationCapabilities } from "../capability/useOrganizationCapabilities";
import { usePlatformCapabilities } from "../capability/usePlatformCapabilities";

/**
 * Whether the current user may open the audit log at a given context level.
 * Checks the level's capability set via `hasAuditRead` (the level's own
 * `<LEVEL>_AUDIT:READ` key or an ancestor level's — the resolver's ancestor walk
 * folds ancestor grants into descendant sets). Strict RBAC — the audit-read key
 * is the only authorization input; the S0 grants give every legacy principal who
 * used to reach the audit pages the matching key. See docs/capabilities.md.
 */
export const useAuditLogPermissions = () => {
  const platformCapabilities = usePlatformCapabilities();
  const organizationCapabilities = useOrganizationCapabilities();
  const leagueCapabilities = useLeagueCapabilities();

  const canSeeAuditLogs = useCallback(
    (level: ScopeLevel) => {
      const setByLevel: Record<ScopeLevel, CapabilitySet> = {
        platform: platformCapabilities,
        organization: organizationCapabilities,
        league: leagueCapabilities,
      };

      return hasAuditRead(setByLevel[level], level);
    },
    [platformCapabilities, organizationCapabilities, leagueCapabilities]
  );

  return { canSeeAuditLogs };
};
